import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  ModalWrapper,
  FormWrapper,
  FieldWrapper,
  Input,
  ErrorMessage,
  Backdrop,
  FooterLinksWrapper,
  FooterLinkItem,
  PasswordToggle,
  PasswordIcon,
} from "./FormModal.styles";
import { FormModalProps } from "./FormModal.types";
import Button from "../../Buttons/Button";
import { eyeClosed, eyeIcon } from "assets/icons";

const EMPTY_ERRORS: Record<string, string> = {};

const FormModal: React.FC<FormModalProps> = ({
  title,
  fields,
  onSubmit,
  hasBackdrop = true,
  footerLinks = [],
  submitButtonText = "Submit",
  externalErrors = EMPTY_ERRORS,
  isLoading = false,
  headerContent,
  beforeSubmitContent,
  submitDisabled = false,
}) => {
  const [formData, setFormData] = useState<Record<string, string>>(
    fields.reduce<Record<string, string>>((acc, field) => {
      acc[field.name] = field.value || "";
      return acc;
    }, {}),
  );
  const [errors, setErrors] = useState<Record<string, string | null>>(externalErrors);
  const [passwordVisibility, setPasswordVisibility] = useState<Record<string, boolean>>({});
  const [isSubmitDisabled, setIsSubmitDisabled] = useState<boolean>(false);
  const { t } = useTranslation();

  useEffect(() => {
    setFormData((current) => {
      const next: Record<string, string> = {};
      fields.forEach((field) => {
        next[field.name] = current[field.name] ?? field.value ?? "";
      });
      return next;
    });
  }, [fields]);

  useEffect(() => {
    setErrors((prevErrors) => ({ ...prevErrors, ...externalErrors }));
  }, [externalErrors]);

  useEffect(() => {
    const allRequiredFilled = fields.every(
      (field) => !field.required || Boolean(formData[field.name]?.trim()),
    );
    setIsSubmitDisabled(!allRequiredFilled);
  }, [formData, fields]);

  const handleChange = (name: string, value: string) => {
    setFormData((current) => ({ ...current, [name]: value }));
    if (errors[name]) {
      setErrors((current) => ({ ...current, [name]: null }));
    }
  };

  const handleSubmit = () => {
    const newErrors: Record<string, string | null> = {};
    fields.forEach((field) => {
      let error: string | null = null;
      if (field.rules) {
        for (const rule of field.rules) {
          error = rule(formData[field.name] || "");
          if (error) break;
        }
      }
      if (!error && field.validate) {
        error = field.validate(formData[field.name] || "");
      }
      newErrors[field.name] = error;
    });
    setErrors(newErrors);
    if (Object.values(newErrors).every((error) => error === null)) {
      onSubmit(formData);
    }
  };

  const togglePasswordVisibility = (fieldName: string) => {
    setPasswordVisibility((prev) => ({ ...prev, [fieldName]: !prev[fieldName] }));
  };

  return (
    <>
      {hasBackdrop && <Backdrop />}
      <ModalWrapper>
        <FormWrapper>
          <h2>{title}</h2>
          {headerContent}
          <form
            onSubmit={(event) => {
              event.preventDefault();
              if (!isSubmitDisabled && !submitDisabled && !isLoading) {
                handleSubmit();
              }
            }}
          >
            {errors._form && <ErrorMessage role="alert">{errors._form}</ErrorMessage>}
            {fields.map((field) => (
              <FieldWrapper key={field.name}>
                {errors[field.name] && <ErrorMessage>{errors[field.name]}</ErrorMessage>}
                <div style={{ position: "relative" }}>
                  <Input
                    id={field.name}
                    type={
                      field.type === "password" && passwordVisibility[field.name]
                        ? "text"
                        : field.type
                    }
                    placeholder={field.placeholder}
                    value={formData[field.name] || ""}
                    hasError={Boolean(errors[field.name])}
                    onChange={(event) => handleChange(field.name, event.target.value)}
                    width={field.width}
                    height={field.height}
                  />
                  {field.type === "password" && (
                    <PasswordToggle
                      type="button"
                      onClick={() => togglePasswordVisibility(field.name)}
                      aria-label={
                        passwordVisibility[field.name]
                          ? t("auth.passwordHide")
                          : t("auth.passwordShow")
                      }
                    >
                      <PasswordIcon
                        as={passwordVisibility[field.name] ? eyeClosed : eyeIcon}
                        aria-hidden="true"
                      />
                    </PasswordToggle>
                  )}
                </div>
              </FieldWrapper>
            ))}
            {beforeSubmitContent}
            <Button
              type="submit"
              variant="primary"
              fullWidth
              disabled={isSubmitDisabled || submitDisabled}
              loading={isLoading}
            >
              {isLoading ? t("Ожидание") : submitButtonText}
            </Button>
          </form>
          {footerLinks.length > 0 && (
            <FooterLinksWrapper>
              {footerLinks.map((link, index) => (
                <FooterLinkItem key={index} href={link.href} variant={link.variant}>
                  {link.text}
                </FooterLinkItem>
              ))}
            </FooterLinksWrapper>
          )}
        </FormWrapper>
      </ModalWrapper>
    </>
  );
};

export default FormModal;
